import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { sendInvoiceEmail, getEmailStatus } from "../services/invoiceEmailService";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { overlayClose } from "../../../../shared/utils/overlayClose";

const OVERLAY = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)",
  zIndex: 1000,
};
const MODAL_BASE = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  background: "var(--bg-card)",
  border: "1px solid var(--border-card)",
  borderRadius: 12,
  padding: "24px",
  zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
};

/**
 * Modal para enviar correo de facturas.
 *
 * Props:
 *   facturas    – array de objetos factura (deben tener id_factura, numero_factura, monto, filial)
 *   onClose()   – cerrar
 *   onSuccess() – callback cuando se envió al menos uno correctamente
 */
export default function SendEmailModal({ facturas = [], onClose, onSuccess }) {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [polling, setPolling] = useState(false);
  const [result,  setResult]  = useState(null);
  const [idioma,  setIdioma]  = useState('es');
  const pollRef               = useRef(null);

  const multiple = facturas.length > 1;

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  async function handleSend() {
    setSending(true);
    try {
      const ids  = facturas.map((f) => f.id_factura ?? f.id);
      const data = await sendInvoiceEmail(ids, idioma);

      if (!data.task_id) {
        setResult(data);
        if (data.total_enviados > 0 && onSuccess) onSuccess();
        return;
      }

      const taskId = data.task_id;
      setSending(false);
      setPolling(true);

      pollRef.current = setInterval(async () => {
        try {
          const status = await getEmailStatus(taskId);
          if (status.done) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPolling(false);
            setResult(status);
            if (status.total_enviados > 0 && onSuccess) onSuccess();
          }
        } catch {
          // Ignorar errores transitorios de red
        }
      }, 2000);

    } catch (e) {
      setResult({
        enviados: [],
        errores: [{ factura: "—", error: e.message }],
        total_enviados: 0,
        total_errores: 1,
      });
    } finally {
      setSending(false);
    }
  }

  // ── Vista de resultado ───────────────────────────────────────────────
  if (result) {
    return (
      <>
        <div {...overlayClose(onClose)} style={OVERLAY} />
        <div style={{ ...MODAL_BASE, width: "min(500px,93vw)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  background: "rgba(249,115,22,0.15)",
                  border: "1px solid rgba(249,115,22,0.3)",
                  borderRadius: 9,
                  padding: "8px 10px",
                  color: "#f97316",
                }}
              >
                <i className="fa-solid fa-envelope" style={{ fontSize: "1.1rem" }} />
              </div>
              <h3
                style={{
                  margin: 0,
                  color: "var(--text-primary)",
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                {t("emails.resultadoEnvio")}
              </h3>
            </div>
            <CloseBtn onClick={onClose} />
          </div>

          <div style={{ borderTop: "1px solid var(--border-card)", marginBottom: 20 }} />

          {/* Resumen */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                background: "rgba(74,222,128,0.08)",
                border: "1px solid rgba(74,222,128,0.25)",
                borderRadius: 8,
                padding: "12px 16px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#4ade80" }}>
                {result.total_enviados}
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                {t("emails.enviadosCorrectamente")}
              </div>
            </div>
            <div
              style={{
                background:
                  result.total_errores > 0
                    ? "rgba(239,68,68,0.08)"
                    : "var(--hover-bg)",
                border: `1px solid ${result.total_errores > 0 ? "rgba(239,68,68,0.25)" : "var(--border-card)"}`,
                borderRadius: 8,
                padding: "12px 16px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: 800,
                  color: result.total_errores > 0 ? "#f87171" : "var(--text-muted)",
                }}
              >
                {result.total_errores}
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                {t("emails.conError")}
              </div>
            </div>
          </div>

          {/* Detalles de enviados */}
          {result.enviados.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                {t("emails.enviados")}
              </div>
              {result.enviados.map((e, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    borderRadius: 6,
                    background: "rgba(74,222,128,0.06)",
                    marginBottom: 4,
                    fontSize: "0.83rem",
                  }}
                >
                  <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                    {e.factura}
                  </span>
                  <span style={{ color: "#4ade80" }}>{e.destinatario}</span>
                </div>
              ))}
            </div>
          )}

          {/* Detalles de errores */}
          {result.errores.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#f87171",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                {t("emails.errores")}
              </div>
              {result.errores.map((e, i) => (
                <div
                  key={i}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 6,
                    background: "rgba(239,68,68,0.07)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    marginBottom: 4,
                    fontSize: "0.82rem",
                  }}
                >
                  <span style={{ color: "#fca5a5", fontWeight: 600 }}>{e.factura}</span>
                  <span style={{ color: "#94a3b8", marginLeft: 8 }}>— {e.error}</span>
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              borderTop: "1px solid var(--border-card)",
              marginTop: 20,
              paddingTop: 16,
              textAlign: "right",
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "8px 20px",
                background: "#f97316",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                fontSize: "0.84rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t("common.cerrar")}
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Vista de envío en background (polling) ───────────────────────────
  if (polling) {
    return (
      <>
        <div style={OVERLAY} />
        <div
          style={{
            ...MODAL_BASE,
            width: "min(400px,93vw)",
            textAlign: "center",
            padding: "40px 32px",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              margin: "0 auto 20px",
              background: "rgba(249,115,22,0.12)",
              border: "1px solid rgba(249,115,22,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="fa-solid fa-circle-notch fa-spin"
              style={{ fontSize: "1.4rem", color: "#f97316" }}
            />
          </div>
          <h3
            style={{
              margin: "0 0 8px",
              color: "var(--text-primary)",
              fontSize: "1rem",
              fontWeight: 700,
            }}
          >
            {t("emails.enviandoCorreos")}
          </h3>
          <p style={{ margin: "0 0 6px", color: "var(--text-secondary)", fontSize: "0.83rem" }}>
            {t("emails.generandoPdfs")}
          </p>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "0.78rem" }}>
            {t("emails.puedesContinuar")}
          </p>
        </div>
      </>
    );
  }

  // ── Vista de confirmación ────────────────────────────────────────────
  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY} />
      <div style={{ ...MODAL_BASE, width: "min(480px,93vw)" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.3)",
                borderRadius: 9,
                padding: "8px 10px",
                color: "#f97316",
              }}
            >
              <i className="fa-solid fa-envelope" style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "var(--text-primary)",
                  fontSize: "1rem",
                  fontWeight: 700,
                }}
              >
                {multiple
                  ? t("emails.enviarNCorreos", { n: facturas.length })
                  : t("emails.enviarCorreoFactura")}
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  color: "var(--text-muted)",
                  fontSize: "0.77rem",
                }}
              >
                {t("emails.emailPropietario")}
              </p>
            </div>
          </div>
          <CloseBtn onClick={onClose} />
        </div>

        <div style={{ borderTop: "1px solid var(--border-card)", marginBottom: 20 }} />

        {/* Lista de facturas a enviar */}
        <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 16 }}>
          {facturas.map((f, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: 6,
                background: i % 2 === 0 ? "var(--hover-bg)" : "transparent",
                marginBottom: 2,
                fontSize: "0.84rem",
              }}
            >
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {f.numero_factura}
              </span>
              <span style={{ color: "var(--text-secondary)" }}>
                {t("emails.filialLabel", { id: f.filial?.idFilial ?? "—" })}
              </span>
              <span style={{ color: f.moneda === "USD" ? "#60a5fa" : "#f97316", fontWeight: 600 }}>
                {f.moneda === "USD"
                  ? `$${Number(f.monto).toFixed(2)}`
                  : `₡${formatCurrency(f.monto)}`}
              </span>
            </div>
          ))}
        </div>

        {/* Selector de idioma */}
        <div style={{ marginBottom: 16 }}>
          <div style={{
            fontSize: "0.74rem", fontWeight: 700, color: "var(--text-muted)",
            textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8,
          }}>
            <i className="fa-solid fa-language" style={{ marginRight: 6 }} />
            {t("emails.idiomaLabel", "Idioma del correo y PDF")}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { code: "es", flag: "🇨🇷", label: "Español" },
              { code: "en", flag: "🇺🇸", label: "English" },
            ].map(({ code, flag, label }) => (
              <button
                key={code}
                onClick={() => setIdioma(code)}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: idioma === code
                    ? "1.5px solid #f97316"
                    : "1px solid var(--border-card)",
                  background: idioma === code
                    ? "rgba(249,115,22,0.12)"
                    : "transparent",
                  color: idioma === code ? "#f97316" : "var(--text-muted)",
                  fontWeight: idioma === code ? 700 : 400,
                  fontSize: "0.84rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.1rem" }}>{flag}</span>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div
          style={{
            background: "rgba(96,165,250,0.07)",
            border: "1px solid rgba(96,165,250,0.2)",
            borderLeft: "3px solid #60a5fa",
            borderRadius: 6,
            padding: "10px 14px",
            color: "#bfdbfe",
            fontSize: "0.82rem",
            marginBottom: 20,
          }}
        >
          <i className="fa-solid fa-circle-info" style={{ marginRight: 7 }} />
          {t("emails.sinEmailWarning")}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid var(--border-card)",
              borderRadius: 7,
              color: "var(--text-muted)",
              fontSize: "0.84rem",
              cursor: "pointer",
            }}
          >
            {t("common.cancelar")}
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            style={{
              padding: "8px 20px",
              background: sending ? "rgba(249,115,22,0.5)" : "#f97316",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: sending ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            {sending ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> {t("emails.enviando")}
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" />{" "}
                {multiple
                  ? t("emails.enviarNCorreos", { n: facturas.length })
                  : t("emails.enviarCorreo")}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function CloseBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        color: "var(--text-muted)",
        cursor: "pointer",
        fontSize: "1.1rem",
      }}
      onMouseOver={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
      onMouseOut={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
    >
      ✕
    </button>
  );
}
